/**
 * Plot inventory store — the demo stand-in for a backend.
 *
 * Deliberately shaped like a real API so it can be swapped for Supabase (or any
 * server) without touching a component: every read goes through a selector and
 * every write goes through an action that returns a result object. The only
 * thing that changes on a real backend is the body of these functions.
 *
 * Two storage layers, and the split is the whole trick:
 *
 *   sessionStorage  → this TAB's client identity (per-tab, not shared)
 *   localStorage    → the shared plot inventory + orders (shared across tabs)
 *
 * So a second browser tab is a *second client* shopping the same inventory.
 * Hold a site in one tab and it locks in the other, live, via the `storage`
 * event. That is the movie-theatre behavior, with no server running.
 */

import {
  CEMETERIES,
  allPlotIds,
  parsePlotId,
  plotPrice,
  rowBurialType,
} from '../data/cemeteries';

const STATE_KEY = 'sc_cemetery_state_v1';
const SESSION_KEY = 'sc_session_id';

/** How long a selected-but-unpurchased site stays locked to a client. */
export const HOLD_DURATION_MS = 15 * 60 * 1000; // 15 minutes

export const PLOT_STATUS = {
  AVAILABLE: 'available',
  SELECTED: 'selected', // held by *you* — a derived view status, never stored
  HELD: 'held', // held by another client
  PENDING: 'pending', // order placed, awaiting payment/contract
  SOLD: 'sold',
};

export const ORDER_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

// ---------------------------------------------------------------------------
// Server inventory overlay
// ---------------------------------------------------------------------------
// In production the Cloudflare D1 ledger is the source of truth for what has
// actually sold. We overlay it on top of the local store so the map shows real
// availability without the client owning the whole inventory. Both are empty in
// the demo, so nothing changes there.
const serverSold = {}; // cemeteryId -> Set(plotId)
const serverPending = {}; // cemeteryId -> Set(plotId)

// ---------------------------------------------------------------------------
// Session identity (per tab)
// ---------------------------------------------------------------------------

let cachedSessionId = null;

export const getSessionId = () => {
  if (cachedSessionId) return cachedSessionId;
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `client_${Math.random().toString(36).slice(2, 8)}`;
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    cachedSessionId = id;
  } catch {
    // Private browsing / storage blocked — fall back to an in-memory identity.
    cachedSessionId = `client_${Math.random().toString(36).slice(2, 8)}`;
  }
  return cachedSessionId;
};

// ---------------------------------------------------------------------------
// Deterministic seed data
// ---------------------------------------------------------------------------

/** Small seeded PRNG so the demo looks identical on every machine. */
const mulberry32 = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const DEMO_CLIENTS = [
  ['Margaret', 'Whitfield', 'm.whitfield@example.com', '(301) 555-0142'],
  ['Ellis', 'Barrow', 'ellis.barrow@example.com', '(240) 555-0119'],
  ['Rosalind', 'Okafor', 'r.okafor@example.com', '(301) 555-0173'],
  ['Theodore', 'Lam', 'ted.lam@example.com', '(202) 555-0188'],
  ['Junie', 'Castellanos', 'junie.c@example.com', '(301) 555-0164'],
  ['Harold', 'Pettigrew', 'h.pettigrew@example.com', '(240) 555-0151'],
  ['Cora', 'Nakashima', 'cora.n@example.com', '(301) 555-0107'],
  ['Wendell', 'Achebe', 'w.achebe@example.com', '(202) 555-0135'],
  ['Beatrice', 'Solomon', 'b.solomon@example.com', '(301) 555-0196'],
  ['Amos', 'Kirkland', 'amos.k@example.com', '(240) 555-0128'],
];

const DEMO_NOTES = [
  'Family requests an east-facing marker.',
  'Pre-need purchase. Deed mailed 3/14.',
  'Adjacent to parents’ sites — keep together.',
  'Veteran; VA marker application on file.',
  'Paid in full. Perpetual care included.',
  'Balance on 12-month payment plan.',
  '',
  '',
];

/**
 * Build the starting inventory: a believable mix of sold and pending sites so
 * the map has texture and the admin console has records to show.
 */
const buildSeedState = () => {
  // Production ships a clean, empty ledger — the D1 backend is the source of
  // truth, and the seeded demo inventory would otherwise mark real sites as
  // "sold" and block their sale. Set REACT_APP_DEMO_INVENTORY=false to disable.
  if (process.env.REACT_APP_DEMO_INVENTORY === 'false') {
    return { plots: {}, orders: [], version: 1 };
  }

  const rand = mulberry32(20250714);
  const plots = {};
  const orders = [];
  let orderSeq = 1000;

  for (const cemetery of CEMETERIES) {
    plots[cemetery.id] = {};

    const ids = allPlotIds(cemetery);
    // Older cemeteries fill from the lower rows up, so weight sales toward the
    // bottom of the map rather than scattering them uniformly.
    const rows = cemetery.layout.rows;
    const sold = new Set();
    for (const id of ids) {
      const { row } = parsePlotId(id);
      const rowIndex = rows.indexOf(row);
      const depth = 1 - rowIndex / rows.length; // 1.0 at row A → ~0 at row N
      const chance = 0.08 + depth * 0.42;
      if (rand() < chance) sold.add(id);
    }

    // Group sold sites into orders — families buy 1–3 adjacent sites.
    const soldList = [...sold];
    let i = 0;
    while (i < soldList.length) {
      const size = rand() < 0.35 ? 2 : 1;
      const group = soldList.slice(i, i + size);
      i += size;

      const [firstName, lastName, email, phone] =
        DEMO_CLIENTS[Math.floor(rand() * DEMO_CLIENTS.length)];
      const isPending = rand() < 0.06;
      const orderId = `SC-${(orderSeq += 1)}`;

      const total = group.reduce((sum, plotId) => {
        const { row } = parsePlotId(plotId);
        return sum + plotPrice(cemetery, row);
      }, 0);
      const care = Math.round(total * cemetery.pricing.careFeeRate);

      // Spread purchase dates over the last ~2 years.
      const daysAgo = Math.floor(rand() * 730) + 1;
      const createdAt = new Date('2026-07-14T12:00:00Z').getTime() - daysAgo * 86400000;

      orders.push({
        id: orderId,
        cemeteryId: cemetery.id,
        plotIds: group,
        status: isPending ? ORDER_STATUS.PENDING : ORDER_STATUS.PAID,
        client: { firstName, lastName, email, phone },
        notes: DEMO_NOTES[Math.floor(rand() * DEMO_NOTES.length)],
        subtotal: total,
        careFee: care,
        total: total + care,
        paymentRef: isPending ? null : `pi_demo_${Math.floor(rand() * 1e9).toString(36)}`,
        createdAt,
        isSeed: true,
      });

      for (const plotId of group) {
        plots[cemetery.id][plotId] = {
          status: isPending ? PLOT_STATUS.PENDING : PLOT_STATUS.SOLD,
          orderId,
        };
      }
    }
  }

  return { plots, orders, version: 1 };
};

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

let state = null;
const listeners = new Set();

const readPersisted = () => {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1 || !parsed.plots) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persist = () => {
  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or blocked — the demo still works for this tab.
  }
};

const ensureState = () => {
  if (!state) state = readPersisted() || buildSeedState();
  return state;
};

const emit = () => listeners.forEach((fn) => fn());

const commit = () => {
  persist();
  emit();
};

/**
 * Drop holds that have run out the clock. Returns true if anything changed.
 * Expiry is computed on read rather than scheduled on write, so a hold placed
 * in a tab that was later closed still expires correctly.
 */
const sweepExpiredHolds = (now = Date.now()) => {
  ensureState();
  let changed = false;
  for (const cemeteryId of Object.keys(state.plots)) {
    for (const [plotId, record] of Object.entries(state.plots[cemeteryId])) {
      if (record.status === PLOT_STATUS.HELD && record.holdExpiresAt <= now) {
        delete state.plots[cemeteryId][plotId];
        changed = true;
      }
    }
  }
  return changed;
};

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

let sweepTimer = null;

const startSweeping = () => {
  if (sweepTimer) return;
  sweepTimer = window.setInterval(() => {
    if (sweepExpiredHolds()) commit();
  }, 1000);
};

const stopSweeping = () => {
  if (!sweepTimer) return;
  window.clearInterval(sweepTimer);
  sweepTimer = null;
};

// Another tab wrote to the inventory — pull it in and re-render.
const onStorage = (event) => {
  if (event.key !== STATE_KEY) return;
  state = readPersisted() || buildSeedState();
  emit();
};

export const subscribe = (fn) => {
  ensureState();
  if (listeners.size === 0) {
    window.addEventListener('storage', onStorage);
    startSweeping();
  }
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      window.removeEventListener('storage', onStorage);
      stopSweeping();
    }
  };
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Raw stored record for a plot, or null if it has never been touched. */
const getRecord = (cemeteryId, plotId) => {
  ensureState();
  return state.plots[cemeteryId]?.[plotId] || null;
};

/**
 * The status of a plot *as seen by a given viewer*. This is where "held by me"
 * and "held by someone else" diverge — the same stored record renders as a
 * selection to its owner and as a lock to everyone else.
 */
export const getPlotStatus = (cemeteryId, plotId, viewerId = getSessionId()) => {
  // The server ledger wins: a site sold or reserved on the backend is
  // unavailable to everyone, whatever the local store thinks.
  if (serverSold[cemeteryId]?.has(plotId)) return PLOT_STATUS.SOLD;
  if (serverPending[cemeteryId]?.has(plotId)) return PLOT_STATUS.PENDING;

  const record = getRecord(cemeteryId, plotId);
  if (!record) return PLOT_STATUS.AVAILABLE;

  if (record.status === PLOT_STATUS.HELD) {
    if (record.holdExpiresAt <= Date.now()) return PLOT_STATUS.AVAILABLE;
    return record.holderId === viewerId ? PLOT_STATUS.SELECTED : PLOT_STATUS.HELD;
  }
  return record.status;
};

/** Full detail for one plot: identity, type, price, status. */
export const getPlot = (cemetery, plotId, viewerId = getSessionId()) => {
  const { row, number } = parsePlotId(plotId);
  return {
    id: plotId,
    row,
    number,
    burialType: rowBurialType(cemetery, row),
    price: plotPrice(cemetery, row),
    status: getPlotStatus(cemetery.id, plotId, viewerId),
  };
};

/** Plot ids currently held by this tab's client, in selection order. */
export const getSelection = (cemeteryId, viewerId = getSessionId()) => {
  ensureState();
  const records = state.plots[cemeteryId] || {};
  const now = Date.now();
  return Object.entries(records)
    .filter(
      ([, r]) =>
        r.status === PLOT_STATUS.HELD && r.holderId === viewerId && r.holdExpiresAt > now,
    )
    .sort((a, b) => a[1].heldAt - b[1].heldAt)
    .map(([plotId]) => plotId);
};

/** When does this client's earliest-expiring hold lapse? null if no holds. */
export const getHoldExpiry = (cemeteryId, viewerId = getSessionId()) => {
  ensureState();
  const records = state.plots[cemeteryId] || {};
  const now = Date.now();
  const expiries = Object.values(records)
    .filter(
      (r) => r.status === PLOT_STATUS.HELD && r.holderId === viewerId && r.holdExpiresAt > now,
    )
    .map((r) => r.holdExpiresAt);
  return expiries.length ? Math.min(...expiries) : null;
};

/** Availability counts for the map legend and the cemetery card. */
export const getAvailability = (cemetery, viewerId = getSessionId()) => {
  const counts = { available: 0, selected: 0, held: 0, pending: 0, sold: 0, total: 0 };
  for (const plotId of allPlotIds(cemetery)) {
    counts[getPlotStatus(cemetery.id, plotId, viewerId)] += 1;
    counts.total += 1;
  }
  return counts;
};

/**
 * Overlay the backend's sold/pending ledger onto the map. Shape:
 * { "<cemeteryId>": { sold: ["A-1", ...], pending: ["C-3", ...] } }.
 * Called with real data in production; a no-op (empty) in the demo.
 */
export const applyServerInventory = (byCemetery) => {
  if (!byCemetery || typeof byCemetery !== 'object') return;
  for (const [cemeteryId, entry] of Object.entries(byCemetery)) {
    serverSold[cemeteryId] = new Set(entry?.sold || []);
    serverPending[cemeteryId] = new Set(entry?.pending || []);
  }
  emit();
};

export const getOrders = (cemeteryId = null) => {
  ensureState();
  const orders = cemeteryId
    ? state.orders.filter((o) => o.cemeteryId === cemeteryId)
    : state.orders;
  return [...orders].sort((a, b) => b.createdAt - a.createdAt);
};

export const getOrder = (orderId) => {
  ensureState();
  return state.orders.find((o) => o.id === orderId) || null;
};

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

const fail = (reason) => ({ ok: false, reason });
const done = (data = {}) => ({ ok: true, ...data });

/**
 * Select a site — places a hold on it for this client.
 * Rejects anything already sold, pending, or held by someone else. That check
 * is the reason two tabs cannot claim the same site.
 */
export const holdPlot = (cemeteryId, plotId) => {
  ensureState();
  sweepExpiredHolds();

  const viewerId = getSessionId();
  const status = getPlotStatus(cemeteryId, plotId, viewerId);

  if (status === PLOT_STATUS.SOLD) return fail('This site has already been sold.');
  if (status === PLOT_STATUS.PENDING) return fail('This site is reserved pending a sale.');
  if (status === PLOT_STATUS.HELD) {
    return fail('Another family is holding this site right now.');
  }
  if (status === PLOT_STATUS.SELECTED) return done({ alreadyHeld: true });

  if (!state.plots[cemeteryId]) state.plots[cemeteryId] = {};
  const now = Date.now();
  state.plots[cemeteryId][plotId] = {
    status: PLOT_STATUS.HELD,
    holderId: viewerId,
    heldAt: now,
    holdExpiresAt: now + HOLD_DURATION_MS,
  };
  commit();
  return done();
};

/** Deselect a site this client is holding. */
export const releasePlot = (cemeteryId, plotId) => {
  ensureState();
  const record = getRecord(cemeteryId, plotId);
  const viewerId = getSessionId();
  if (!record || record.status !== PLOT_STATUS.HELD || record.holderId !== viewerId) {
    return fail('You are not holding this site.');
  }
  delete state.plots[cemeteryId][plotId];
  commit();
  return done();
};

export const togglePlot = (cemeteryId, plotId) => {
  const status = getPlotStatus(cemeteryId, plotId);
  return status === PLOT_STATUS.SELECTED
    ? releasePlot(cemeteryId, plotId)
    : holdPlot(cemeteryId, plotId);
};

export const releaseSelection = (cemeteryId) => {
  ensureState();
  for (const plotId of getSelection(cemeteryId)) {
    delete state.plots[cemeteryId][plotId];
  }
  commit();
  return done();
};

/** Push every hold this client holds out to a fresh full duration. */
export const extendHolds = (cemeteryId) => {
  ensureState();
  const viewerId = getSessionId();
  const expiresAt = Date.now() + HOLD_DURATION_MS;
  for (const plotId of getSelection(cemeteryId, viewerId)) {
    state.plots[cemeteryId][plotId].holdExpiresAt = expiresAt;
  }
  commit();
  return done({ expiresAt });
};

/**
 * Convert this client's held sites into an order.
 * Re-validates every site under the hold, so a hold that lapsed mid-checkout
 * fails here rather than double-selling a plot.
 */
export const createOrder = (cemetery, { client, notes = '' }) => {
  ensureState();
  sweepExpiredHolds();

  const viewerId = getSessionId();
  const plotIds = getSelection(cemetery.id, viewerId);
  if (plotIds.length === 0) {
    return fail('Your hold expired before checkout completed. Please select your sites again.');
  }

  const subtotal = plotIds.reduce((sum, plotId) => {
    const { row } = parsePlotId(plotId);
    return sum + plotPrice(cemetery, row);
  }, 0);
  const careFee = Math.round(subtotal * cemetery.pricing.careFeeRate);

  const order = {
    id: `SC-${Date.now().toString().slice(-6)}`,
    cemeteryId: cemetery.id,
    plotIds,
    status: ORDER_STATUS.PENDING,
    client,
    notes,
    subtotal,
    careFee,
    total: subtotal + careFee,
    paymentRef: null,
    createdAt: Date.now(),
  };

  state.orders.push(order);
  for (const plotId of plotIds) {
    state.plots[cemetery.id][plotId] = { status: PLOT_STATUS.PENDING, orderId: order.id };
  }
  commit();
  return done({ order });
};

/** Mark an order paid and flip its sites to sold. */
export const markOrderPaid = (orderId, paymentRef) => {
  ensureState();
  const order = getOrder(orderId);
  if (!order) return fail('Order not found.');

  order.status = ORDER_STATUS.PAID;
  order.paymentRef = paymentRef || `pi_demo_${Math.random().toString(36).slice(2, 10)}`;
  for (const plotId of order.plotIds) {
    state.plots[order.cemeteryId][plotId] = { status: PLOT_STATUS.SOLD, orderId: order.id };
  }
  commit();
  return done({ order });
};

/** Admin: cancel an order and return its sites to the available pool. */
export const cancelOrder = (orderId) => {
  ensureState();
  const order = getOrder(orderId);
  if (!order) return fail('Order not found.');

  order.status = ORDER_STATUS.CANCELLED;
  for (const plotId of order.plotIds) {
    delete state.plots[order.cemeteryId][plotId];
  }
  commit();
  return done({ order });
};

/** Admin: edit the client record or notes on an order. */
export const updateOrder = (orderId, patch) => {
  ensureState();
  const order = getOrder(orderId);
  if (!order) return fail('Order not found.');

  if (patch.notes !== undefined) order.notes = patch.notes;
  if (patch.client) order.client = { ...order.client, ...patch.client };
  if (patch.status && patch.status !== order.status) {
    if (patch.status === ORDER_STATUS.PAID) return markOrderPaid(orderId);
    if (patch.status === ORDER_STATUS.CANCELLED) return cancelOrder(orderId);
    if (patch.status === ORDER_STATUS.PENDING) {
      order.status = ORDER_STATUS.PENDING;
      for (const plotId of order.plotIds) {
        state.plots[order.cemeteryId][plotId] = {
          status: PLOT_STATUS.PENDING,
          orderId: order.id,
        };
      }
    }
  }
  commit();
  return done({ order });
};

/** Wipe all demo state and reseed — handy between investor walkthroughs. */
export const resetDemo = () => {
  state = buildSeedState();
  commit();
  return done();
};
